//
// Copyright (c) 2011-2024 by Curalate, Inc
//

import { useInfiniteQuery } from '@tanstack/react-query';

import { getContentLibraryAutocomplete } from '@apis/content-library/contentLibraryApi';
import {
  IContentLibraryAutocomplete,
  IContentLibraryAutocompleteRequest,
} from '@apis/content-library/contentLibraryApiModel';
import { QueryKeyConstants } from '@queries/QueryKeyConstants';
import { TWENTY_SECONDS_STALE_TIME } from '@queries/QueryOptionsConstants';
import { ErrorType, getErrorMessage } from '@utils/errorMessages';

const MEDIA_LIBRARY_LABELS_DEFAULT_LIMIT = 12;

export interface LabelsQueryParams {
  query: IContentLibraryAutocompleteRequest;
}

export const useMediaLibraryLabelsInfiniteQuery = ({
  query,
}: LabelsQueryParams) => {
  const limit = query.limit ?? MEDIA_LIBRARY_LABELS_DEFAULT_LIMIT;

  const fetchContentLibraryAutocompleteItems = async (
    page = 0,
    signal?: AbortSignal,
  ) => {
    try {
      const result = await getContentLibraryAutocomplete(
        {
          ...query,
          limit,
          offset: page > 0 ? (page - 1) * limit : 0,
        },
        { signal },
      );
      return result.labels;
    } catch (error) {
      throw new Error(getErrorMessage(ErrorType.LABELS_FETCH, error));
    }
  };

  return useInfiniteQuery<IContentLibraryAutocomplete[], Error>({
    queryKey: [
      QueryKeyConstants.MediaFilters,
      QueryKeyConstants.MediaFiltersLabels,
      query,
    ],
    queryFn: async ({ pageParam, signal }) =>
      await fetchContentLibraryAutocompleteItems(pageParam, signal),
    getNextPageParam: (last, pages) =>
      last.length < limit ? undefined : pages.length + 1,
    staleTime: TWENTY_SECONDS_STALE_TIME,
    keepPreviousData: true,
  });
};
